import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import { CategoryListUrlSortField } from "@saleor/categories/urls";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { CategoryFragment } from "@saleor/fragments/types/CategoryFragment";
import { SubmitPromise } from "@saleor/hooks/useForm";
import { maybe, renderCollection } from "@saleor/misc";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage } from "react-intl";

import CategoryFeaturedCheckbox from "../CategoryFeaturedCheckbox";

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colFeatures: {
        width: 200
      },
      colName: {
        width: 300
      },
      colProducts: {
        width: 160
      },
      colSubcategories: {
        width: 240
      },
      colTypeLevel: {
        width: 210
      }
    },
    colFeatures: {
      textAlign: "left"
    },
    colName: {
      paddingLeft: 0
    },
    colProducts: {
      textAlign: "center"
    },
    colSubcategories: {
      textAlign: "center"
    },
    collectionTbl: {
      "@media (max-width: 767px)": {
        width: "800px"
      }
    },
    tableRow: {
      cursor: "pointer"
    }
  }),
  { name: "CategoryList" }
);

interface CategoryListProps
  extends ListProps,
    ListActions,
    SortPage<CategoryListUrlSortField> {
  categories?: CategoryFragment[];
  initialSearch?: string | null;
  isRoot: boolean;
  mainCategory?: boolean;
  onSubmit?: (data) => SubmitPromise;
  onAdd?();
}

const numberOfColumns = 5;

const CategoryList: React.FC<CategoryListProps> = props => {
  const {
    categories,
    disabled,
    settings,
    sort,
    pageInfo,
    mainCategory,
    isChecked,
    isRoot,
    selected,
    toggle,
    toggleAll,
    toolbar,
    onSubmit,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRowClick,
    onSort
  } = props;

  const classes = useStyles(props);
  return (
    <ResponsiveTable className={classes.collectionTbl}>
      <TableHead
        colSpan={numberOfColumns}
        selected={selected}
        disabled={disabled}
        items={categories}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader
          direction={
            isRoot && sort.sort === CategoryListUrlSortField.name
              ? getArrowDirection(sort.asc)
              : undefined
          }
          arrowPosition="right"
          className={classes.colName}
          disableClick={!isRoot}
          onClick={() => isRoot && onSort(CategoryListUrlSortField.name)}
        >
          <FormattedMessage defaultMessage="Category Name" />
        </TableCellHeader>

        <TableCellHeader className={classes.colTypeLevel}>
          <FormattedMessage
            defaultMessage="Store Type"
            description="store type"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            isRoot && sort.sort === CategoryListUrlSortField.subcategoryCount
              ? getArrowDirection(sort.asc)
              : undefined
          }
          className={classes.colSubcategories}
          disableClick={!isRoot}
          onClick={() =>
            isRoot && onSort(CategoryListUrlSortField.subcategoryCount)
          }
        >
          <FormattedMessage
            defaultMessage="No. of Sub-Categories"
            description="number of subcategories"
          />
        </TableCellHeader>
        <TableCellHeader
          direction={
            isRoot && sort.sort === CategoryListUrlSortField.productCount
              ? getArrowDirection(sort.asc)
              : undefined
          }
          className={classes.colProducts}
          disableClick={!isRoot}
          onClick={() =>
            isRoot && onSort(CategoryListUrlSortField.productCount)
          }
        >
          <FormattedMessage
            defaultMessage="No. of Products"
            description="number of products"
          />
        </TableCellHeader>
        {!mainCategory ? (
          <TableCellHeader className={classes.colTypeLevel}>
            <FormattedMessage defaultMessage="Level" description="level" />
          </TableCellHeader>
        ) : (
          <TableCellHeader className={classes.colFeatures}>
            <FormattedMessage
              defaultMessage="Featured "
              description="Featured"
            />
          </TableCellHeader>
        )}
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          categories,
          (category, index) => {
            const isSelected = category ? isChecked(category.id) : false;

            return (
              <TableRow
                className={classes.tableRow}
                hover={!!category}
                onClick={category ? onRowClick(category.id) : undefined}
                key={category ? category.id : "skeleton"}
                selected={isSelected}
                data-test="id"
                data-test-id={maybe(() => category.id)}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    id={`checkboxRow${index}`}
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(category.id)}
                  />
                </TableCell>
                <TableCell
                  className={classes.colName}
                  data-test="name"
                  id={`categoryNameRow${index}`}
                >
                  {category && category.name ? category.name : <Skeleton />}
                </TableCell>

                <TableCell
                  className={classes.colTypeLevel}
                  data-test="store-type"
                  id={`storeTypeRow${index}`}
                >
                  {category && category.storeType ? (
                    category.storeType
                  ) : (
                    <div>NO_STORE_TYPE</div>
                  )}
                </TableCell>
                <TableCell
                  className={classes.colSubcategories}
                  id={`subcategoriesRow${index}`}
                >
                  {category &&
                  category.children &&
                  category.children.totalCount !== undefined ? (
                    category.children.totalCount
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colProducts}
                  id={`productsRow${index}`}
                >
                  {category &&
                  category.products &&
                  category.products.totalCount !== undefined ? (
                    category.products.totalCount
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                {!mainCategory ? (
                  <TableCell
                    className={classes.colTypeLevel}
                    data-test="level"
                    id={`levelRow${index}`}
                  >
                    {category && category.level ? category.level : 0}
                  </TableCell>
                ) : (
                  <TableCell
                    className={classes.colFeatures}
                    onClick={e => e.stopPropagation()}
                    id={`featuredCheckboxRow${index}`}
                  >
                    <div onClick={e => e.stopPropagation()}>
                      <CategoryFeaturedCheckbox
                        onSubmit={onSubmit}
                        id={category?.id}
                        isFeatured={category?.isFeatured}
                      />
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                {isRoot ? (
                  <FormattedMessage defaultMessage="No categories found" />
                ) : (
                  <FormattedMessage defaultMessage="No subcategory found" />
                )}
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

CategoryList.displayName = "CategoryList";
export default CategoryList;
